<template>
  <!-- Page Header -->
  <PageHeader
    class="no-print mb-4 pr-10"
    title="Mes factures"
    subtitle="Récapitulatif des transactions et prestations"
  />

  <div class="facture-print w-full sm:px-6 pt-1 pb-8">
    <!-- En-tête imprimable (seulement pour print) -->
    <div class="print-only mb-6">
      <div>
        <h1 style="font-size:18px; font-weight:700; margin:0;">Facture {{ periodLong }}</h1>
        <div style="font-size:13px; margin-top:4px;">{{ sellerName ? ' - ' + sellerName : '' }}</div>
      </div>
    </div>

    <!-- Sélecteur de type (mensuel / bimensuel) + période -->
    <div class="flex flex-wrap justify-start items-center gap-4 mb-4 no-print">
      <!-- Toggle mensuel / bimensuel -->
      <div class="flex rounded-md border border-gray-300 overflow-hidden">
        <button
          class="px-4 py-2 text-sm font-medium transition-colors border-r border-gray-300"
          :class="factureType === 'bimensuel' ? 'bg-indigo-600 text-white' : 'bg-white text-black hover:bg-gray-50'"
          @click="switchType('bimensuel')"
        >
          Bimensuel
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors"
          :class="factureType === 'mensuel' ? 'bg-indigo-600 text-white' : 'bg-white text-black hover:bg-gray-50'"
          @click="switchType('mensuel')"
        >
          Mensuel
        </button>
      </div>

      <!-- Sélecteur de période -->
      <div class="flex items-center">
        <label for="period-select" class="text-sm font-medium text-black pr-2">Période :</label>
        <select
          id="period-select"
          v-model="selectedPeriod"
          @change="handlePeriodChange"
          class="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
        >
          <option v-for="period in availablePeriods" :key="period" :value="period">
            {{ labelPeriod(period) }}
          </option>
        </select>
      </div>
    </div>

    <!-- Tableau principal des transactions (avec collapse) -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-lg font-medium text-black">Transactions de {{ periodLong }}</h3>
        <button
          class="inline-flex items-center gap-2 text-sm text-black hover:text-black"
          :aria-expanded="txOpen.toString()"
          :title="txOpen ? 'Réduire' : 'Développer'"
          @click="txOpen = !txOpen"
        >
          <span>{{ txOpen ? 'Réduire' : 'Afficher' }}</span>
          <i class="fas fa-chevron-down transition-transform duration-200" :class="txOpen ? 'rotate-0' : '-rotate-90'"></i>
        </button>
      </div>
      <div class="overflow-x-auto collapsible-print" v-show="txOpen">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Date</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Société</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Adresse de facturation</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-black uppercase tracking-wider">Montant HT</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-black uppercase tracking-wider">TVA ({{ currentData.summary.tvaRateLabel }})</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-black uppercase tracking-wider">Montant Total</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(transaction, index) in currentData.transactions"
              :key="transaction.id"
              class="hover:bg-gray-50"
              :class="{ 'print-only-row': !isTransactionOnPage(index) }"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ formatDate(transaction.date) }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <template v-if="flagCode(transaction)">
                    <img
                      v-if="!hasFlagImgError(flagCode(transaction)!)"
                      :src="flagImgUrl(flagCode(transaction)!)"
                      :alt="flagCode(transaction) || ''"
                      class="mr-2 inline-block align-middle"
                      style="width:18px;height:13px;border-radius:2px;object-fit:cover;"
                      @error="onFlagImgError(flagCode(transaction)!)"
                    />
                    <span v-else class="mr-2 text-base">{{ iso2ToFlag(flagCode(transaction)) || flagCode(transaction) }}</span>
                  </template>
                  <div v-else class="w-4 h-4 bg-gray-300 rounded mr-2"></div>
                  <span class="text-sm font-medium text-black" :title="flagTitle(transaction)">{{ transaction.societe }}</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ transaction.adresseFacturation }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-right">{{ transaction.montantHT.toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-right">
                <template v-if="Number(transaction.tva) === 0">-</template>
                <template v-else>{{ transaction.tva.toFixed(2) }}</template>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-right">{{ transaction.montantTotal.toFixed(2) }}</td>
            </tr>
            <!-- Ligne de total -->
            <tr class="bg-gray-100 font-bold">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black">Total</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black"></td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black"></td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-right">{{ totalMontantHT.toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-right">{{ totalTVA.toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-right">{{ totalMontantTotal.toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
        <!-- Pagination -->
        <div class="ml-4 my-4 no-print">
          <UnifiedPagination
            :current-page="currentPageTx"
            :total-items="currentData.transactions.length"
            :items-per-page="itemsPerPageTx"
            :show-first-last-buttons="true"
            @page-change="handleTxPageChange"
            @items-per-page-change="handleTxItemsPerPageChange"
          />
        </div>
      </div>
    </div>

    <!-- Règlement de la facture -->
    <div v-if="currentData.facture" class="mb-4">
      <div class="border border-green-300 border-dashed rounded-md bg-white" style="padding:14px 16px;">
        <div>
          <h3 class="text-base font-medium text-black mb-1 text-left">
            Règlement de la facture de {{ periodLong }}
          </h3>
          <div class="text-sm text-black text-center">
            <div>
              <span class="font-medium">Date de règlement :</span>
              <span class="ml-2">{{ formatDateTime(currentData.facture.dateReglement) }}</span>
            </div>
            <div>
              <span class="font-medium">Montant du règlement :</span>
              <span class="ml-2 text-green-600 font-semibold">{{ Number(currentData.facture.montantReglement || 0).toFixed(2) }}€</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section inférieure avec prestations et résumé -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
      <!-- Liste des prestations -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 class="text-lg font-medium text-black">Liste des prestations de {{ periodLong }}</h3>
            <button
              class="inline-flex items-center gap-2 text-sm text-black hover:text-black"
              :aria-expanded="servicesOpen.toString()"
              :title="servicesOpen ? 'Réduire' : 'Développer'"
              @click="servicesOpen = !servicesOpen"
            >
              <span>{{ servicesOpen ? 'Réduire' : 'Afficher' }}</span>
              <i class="fas fa-chevron-down transition-transform duration-200" :class="servicesOpen ? 'rotate-0' : '-rotate-90'"></i>
            </button>
          </div>
          <div v-if="currentData.services.length === 0" class="px-6 py-4 text-sm text-black">
            Ce grossiste n'a aucune prestation pour la période <strong>{{ periodLong }}</strong>
          </div>
          <div v-else class="overflow-x-auto collapsible-print" v-show="servicesOpen">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">#</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Libellé</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-black uppercase tracking-wider">Quantité</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-black uppercase tracking-wider">Montant (€)</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="service in currentData.services" :key="service.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ service.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ service.libelle }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-center">{{ service.quantite }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-black text-right">{{ service.montant.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Résumé financier -->
      <div class="lg:col-span-1">
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 class="text-lg font-medium text-black mb-4">Résumé financier</h3>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-black">Prestation service EPF 10% (HT) (€):</span>
              <span class="text-sm font-medium text-black">{{ currentData.summary.prestationServiceEPF.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between items-center" v-if="isBeforeFeb2026 && currentData.summary.remiseCommissionEfpHT > 0">
              <span class="text-sm text-black">Remise Commission EFP HT (€) :</span>
              <span class="text-sm font-medium text-red-600">- {{ currentData.summary.remiseCommissionEfpHT.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-black">Remboursement de la commission bancaire HT (€):</span>
              <span class="text-sm font-medium text-black">{{ currentData.summary.remboursementCommission.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-black">Prestations HT (€):</span>
              <span class="text-sm font-medium text-black">{{ currentData.summary.prestationsHT.toFixed(2) }}</span>
            </div>
            <div class="border-t border-gray-200 pt-3">
              <div class="flex justify-between items-center">
                <span class="text-sm font-medium text-black">Total HT (€):</span>
                <span class="text-sm font-bold text-black">{{ currentData.summary.totalHT.toFixed(2) }}</span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-black">TVA ({{ currentData.summary.tvaRateLabel }}) (€):</span>
              <span class="text-sm font-medium text-black">{{ currentData.summary.tva.toFixed(2) }}</span>
            </div>
            <div class="border-t border-gray-200 pt-3">
              <div class="flex justify-between items-center">
                <span class="text-sm font-bold text-black">Total TTC (€):</span>
                <span class="text-lg font-bold text-black">{{ currentData.summary.totalTTC.toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Boutons d'action -->
    <div class="flex justify-center gap-4 no-print">
      <button
        @click="handleExportExcel"
        class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
      >
        <i class="fas fa-file-excel mr-2"></i>
        Exporter en Excel
      </button>
      <button
        @click="handlePrint"
        class="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
      >
        <i class="fas fa-print mr-2"></i>
        Imprimer le récapitulatif
      </button>
      <a
        v-if="currentData.facture?.noFacture"
        :href="invoiceUrl"
        target="_blank"
        class="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
      >
        <i class="fas fa-file-pdf mr-2"></i>
        Voir la facture{{ currentData.facture.noFacture }}
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { PageHeader, UnifiedPagination } from '@/components'
import { fetchApi, getApiBaseUrl } from '@/config/api'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import { extractAdminCalendarDate } from '@/utils/adminOrderDisplay'
import { getDomTomIsoFromPostalCode } from '@/utils/domTom'

const authStore = useAuthStore()
const router = useRouter()

type TransactionRow = {
  id: number | string
  date: string
  societe: string
  adresseFacturation: string
  montantHT: number
  tva: number
  montantTotal: number
  paysLivraisonCode2?: string | null
  paysLivraisonNom?: string | null
  paysFacturationCode2?: string | null
  paysFacturationNom?: string | null
  codePostalLivraison?: string | null
}
type ServiceRow = { id?: number | string; libelle: string; quantite: number; montant: number }
type SummaryBox = {
  prestationServiceEPF: number
  remiseCommissionEfpHT: number
  remboursementCommission: number
  prestationsHT: number
  totalHT: number
  tva: number
  totalTTC: number
  tvaRateLabel: string
}

// Type de facture : mensuel ou bimensuel
const factureType = ref<'mensuel' | 'bimensuel'>('bimensuel')

const selectedPeriod = ref<string>('')
const availablePeriods = ref<string[]>([])
const sellerName = ref<string>('')

const currentData = ref<{ transactions: TransactionRow[]; services: ServiceRow[]; summary: SummaryBox; facture: any }>({
  transactions: [],
  services: [],
  summary: { prestationServiceEPF: 0, remiseCommissionEfpHT: 0, remboursementCommission: 0, prestationsHT: 0, totalHT: 0, tva: 0, totalTTC: 0, tvaRateLabel: '20%' },
  facture: null
})

const invoiceUrl = computed(() => {
  const noFacture = currentData.value.facture?.noFacture
  const idVendeur = authStore.user?.id
  if (!noFacture || !idVendeur) return ''
  // Passe par le backend : v1 prioritaire (et recopié sur S3), puis S3,
  // puis génération sur S3 si la facture n'existe sur aucun des deux.
  // Le paramètre versionné contourne l'ancienne réponse PDF mise en cache sous
  // localhost ; le backend redirige ensuite vers storage.efashion-paris.com.
  return `${getApiBaseUrl()}/facturePDF/vendeurs/${idVendeur}/${noFacture}FR.pdf?storage=v2`
})

const isBeforeFeb2026 = computed(() => {
  if (!selectedPeriod.value) return true
  return selectedPeriod.value.substring(0, 7) < '2026-02'
})

// Totaux
const totalMontantHT = computed(() => currentData.value.transactions.reduce((s, t) => s + (t.montantHT || 0), 0))
const totalTVA = computed(() => currentData.value.transactions.reduce((s, t) => s + (t.tva || 0), 0))
const totalMontantTotal = computed(() => currentData.value.transactions.reduce((s, t) => s + (t.montantTotal || 0), 0))

// Pagination
const currentPageTx = ref<number>(1)
const itemsPerPageTx = ref<number>(10)
function isTransactionOnPage(index: number): boolean {
  const start = (currentPageTx.value - 1) * itemsPerPageTx.value
  return index >= start && index < start + itemsPerPageTx.value
}
function handleTxPageChange(page: number) { currentPageTx.value = page }
function handleTxItemsPerPageChange(size: number) { itemsPerPageTx.value = size; currentPageTx.value = 1 }

// Collapse states
const txOpen = ref(true)
const servicesOpen = ref(true)

function ym(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function formatDate(d: string | null | undefined): string {
  if (!d) return ''
  const s = d.substring(0, 10)
  const [y, m, day] = s.split('-')
  if (!y || !m || !day) return d as string
  return `${day}/${m}/${y}`
}

function formatDateTime(d: string | null | undefined): string {
  if (!d) return ''
  const date = d.substring(0, 10)
  const time = d.length > 10 ? d.substring(11, 19) : ''
  const [y, m, day] = date.split('-')
  if (!y || !m || !day) return d as string
  return `${day}/${m}/${y}${time ? ' ' + time : ''}`
}

const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']

const periodLong = computed(() => {
  const p = selectedPeriod.value
  if (!p) return ''
  const base = p.substring(0, 7)
  const [y, m] = base.split('-').map(Number)
  const name = months[(m || 1) - 1] || base
  if (factureType.value === 'bimensuel') {
    const demi = p.endsWith('S1') ? ' (S1)' : p.endsWith('S2') ? ' (S2)' : ''
    return `${name} ${y || ''}${demi}`.trim()
  }
  return `${name} ${y || ''}`.trim()
})

function labelPeriod(period: string): string {
  if (factureType.value === 'mensuel' && period === '2025-10-09') return '09 octobre 2025'
  const base = period.substring(0, 7)
  const [y, m] = base.split('-').map(Number)
  const name = months[(m || 1) - 1] || base
  if (factureType.value === 'bimensuel') {
    const demi = period.endsWith('S1') ? ' (S1)' : period.endsWith('S2') ? ' (S2)' : ''
    return `${name} ${y}${demi}`
  }
  return `${name} ${y || ''}`.trim()
}

function buildPeriods(): void {
  const periods: string[] = []
  const now = new Date()

  if (factureType.value === 'mensuel') {
    for (let i = 0; i < 100; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const yym = ym(d)
      if (yym > '2025-10') continue
      if (yym === '2025-10') {
        if (!periods.includes('2025-10-09')) periods.push('2025-10-09')
        continue
      }
      periods.push(yym)
    }
  } else {
    for (let i = 0; i < 100; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const yym = ym(d)
      if (yym < '2025-10') break
      if (yym === '2025-10') {
        periods.push('2025-10')
        break
      } else {
        periods.push(`${yym}-S2`)
        periods.push(`${yym}-S1`)
      }
    }
  }

  availablePeriods.value = periods
  selectedPeriod.value = periods[0]
}

async function fetchVendeurCA(fdate: string) {
  const idVendeur = authStore.user?.id
  const query = `query($id:Int!,$fd:String!){ vendeurCA(id_vendeur:$id, fdate:$fd) }`
  const res = await fetchApi<{ vendeurCA: string }>(query, { id: Number(idVendeur), fd: fdate })
  return res?.vendeurCA ? JSON.parse(res.vendeurCA) : null
}

function round2(n: number): number { return Math.round((Number(n) + Number.EPSILON) * 100) / 100 }

function mapToUI(data: any): void {
  if (!data) {
    currentData.value = {
      transactions: [],
      services: [],
      summary: { prestationServiceEPF: 0, remiseCommissionEfpHT: 0, remboursementCommission: 0, prestationsHT: 0, totalHT: 0, tva: 0, totalTTC: 0, tvaRateLabel: '—' },
      facture: null
    }
    sellerName.value = ''
    return
  }
  sellerName.value = String(data.nomSociete || '')
  const commandes = data.commandes || {}
  const beforeFeb2026 = isBeforeFeb2026.value

  const txs: TransactionRow[] = Object.keys(commandes).map((k) => {
    const c = commandes[k]
    let ht: number
    if (factureType.value === 'mensuel') {
      ht = beforeFeb2026 ? Number(c.montantApresRemise || 0) : Number(c.montantCA || c.montantApresRemise || 0)
    } else {
      const rawCA = Number(c.montantCA)
      ht = rawCA > 0 ? rawCA : Number(c.montantApresRemise || 0)
    }
    const tva = Number(c.tva || 0)
    const ttc = c.montantTTC != null ? Number(c.montantTTC) : ht
    return {
      id: c.id_commande,
      date: extractAdminCalendarDate(c.dateCommande) || '',
      societe: c.nomSociete || '',
      adresseFacturation: c.adresseFacturation || '',
      montantHT: round2(ht),
      tva: round2(tva),
      montantTotal: round2(ttc),
      paysLivraisonCode2: c.pays_livraison_code2 || null,
      paysLivraisonNom: c.pays_livraison_nom || null,
      paysFacturationCode2: c.pays_facturation_code2 || null,
      paysFacturationNom: c.pays_facturation_nom || null,
      codePostalLivraison: c.codepostal_livraison || null,
    }
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  const services: ServiceRow[] = (data.prestationsDetail?.lignes || []).map((p: any) => ({
    id: p.id_vendeur_prestation,
    libelle: p.libelle,
    quantite: Number(p.quantite || 0),
    montant: round2(Number(p.montant || 0))
  }))

  const prestationServiceEPF = Number(data.commissionEFP || 0)
  const remiseCommissionEfpHT = Number(data.RemiseCommissionHT || data.remiseCommissionHT || 0)
  const remboursementCommission = Number(data.commissionBanque || 0)
  const prestationsHT = Number(data.prestationsHT || 0)
  const totalHT = Number(data.montantFactureHT || 0)
  const totalTTC = Number(data.montantFactureTTC || 0)
  const tva = Number(data.montantFactureTva || (totalTTC - totalHT))
  const tvaRate = totalHT > 0 ? (tva / totalHT) : 0
  const tvaRateLabel = `${round2(tvaRate * 100)}%`

  currentData.value = {
    transactions: txs,
    services,
    summary: {
      prestationServiceEPF: round2(prestationServiceEPF),
      remiseCommissionEfpHT: round2(remiseCommissionEfpHT),
      remboursementCommission: round2(remboursementCommission),
      prestationsHT: round2(prestationsHT),
      totalHT: round2(totalHT),
      tva: round2(tva),
      totalTTC: round2(totalTTC),
      tvaRateLabel,
    },
    facture: data.facture ? {
      noFacture: data.facture.no_facture ?? null,
      montantReglement: data.facture.montant_reglement ?? null,
      prestations: data.facture.prestations ?? null,
      dateReglement: data.facture.date_reglement ?? null,
      reglement: data.facture.reglement ?? null,
    } : null
  }
}

async function switchType(type: 'mensuel' | 'bimensuel'): Promise<void> {
  if (factureType.value === type) return
  factureType.value = type
  buildPeriods()
  const data = await fetchVendeurCA(selectedPeriod.value)
  mapToUI(data)
  currentPageTx.value = 1
}

async function handlePeriodChange(): Promise<void> {
  const data = await fetchVendeurCA(selectedPeriod.value)
  mapToUI(data)
  currentPageTx.value = 1
}

async function handlePrint(): Promise<void> {
  await nextTick()
  requestAnimationFrame(() => window.print())
}

function escapeCsvField(v: any): string {
  return '"' + String(v ?? '').replace(/"/g, '""') + '"'
}

function handleExportExcel(): void {
  const headers = ['Date','Société','Adresse de facturation','Montant HT','TVA','Montant TTC']
  const rows = currentData.value.transactions.map((t: any) => [
    formatDate(t.date), t.societe, t.adresseFacturation,
    Number(t.montantHT || 0).toFixed(2),
    Number(t.tva || 0).toFixed(2),
    Number(t.montantTotal || 0).toFixed(2)
  ])
  const lines = ['sep=,', headers.map(escapeCsvField).join(','), ...rows.map((r: any[]) => r.map(escapeCsvField).join(','))]
  const csv = lines.join('\r\n')
  const bom = new Uint8Array([0xEF, 0xBB, 0xBF])
  const blob = new Blob([bom, csv], { type: 'text/csv; charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `facture-${factureType.value}-${selectedPeriod.value}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// Flags helpers
function iso2ToFlag(code?: string | null): string {
  if (!code || code.length !== 2) return ''
  const up = code.toUpperCase()
  return String.fromCodePoint(up.charCodeAt(0) - 65 + 0x1F1E6) + String.fromCodePoint(up.charCodeAt(1) - 65 + 0x1F1E6)
}
function flagImgUrl(code?: string | null): string {
  if (!code || code.length !== 2) return ''
  return `https://flagcdn.com/24x18/${code.toLowerCase()}.png`
}
const failedFlagCodes = ref<Set<string>>(new Set())
function onFlagImgError(code?: string | null): void { if (code) failedFlagCodes.value.add(code.toUpperCase()) }
function hasFlagImgError(code?: string | null): boolean { return !code ? false : failedFlagCodes.value.has(code.toUpperCase()) }
function flagCode(t: TransactionRow): string | null | undefined {
  const base = (t.paysLivraisonCode2 || t.paysFacturationCode2 || '').toUpperCase()
  if (!base) return null
  if (base === 'FR') { const override = getDomTomIsoFromPostalCode(t.codePostalLivraison); if (override) return override }
  return base
}
function flagTitle(t: TransactionRow): string { return (t.paysLivraisonNom || t.paysFacturationNom || '') as string }

onMounted(async () => {
  const ok = await authStore.checkAuth()
  if (!ok) { router.push({ name: 'auth' }); return }
  buildPeriods()
  const data = await fetchVendeurCA(selectedPeriod.value)
  mapToUI(data)
  currentPageTx.value = 1
})
</script>

<style lang="scss" scoped>
@media print {
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  @page { margin: 0; }
  :host, * {
    color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    box-shadow: none !important;
    text-shadow: none !important;
  }
  .rounded-xl, .rounded-md, .shadow-sm { border-radius: 0 !important; box-shadow: none !important; }
  .bg-white, .bg-gray-50, .bg-gray-100 { background: #fff !important; }
  .border, .border-gray-200 { border: 0 !important; }
  table { width: 100% !important; border-collapse: collapse !important; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td { border: 0 !important; padding: 6px 8px !important; font-size: 12px !important; background: #fff !important; }
  th { font-weight: 700 !important; text-transform: none !important; }
  .font-bold, .font-medium { font-weight: 700 !important; }
  .text-right { text-align: right !important; }
  .text-left { text-align: left !important; }
  .text-center { text-align: center !important; }
  .px-6 { padding-left: 12px !important; padding-right: 12px !important; }
  .py-4 { padding-top: 8px !important; padding-bottom: 8px !important; }
  .mb-8, .mb-10 { margin-bottom: 12px !important; }
  .hover\:bg-gray-50 { background: #fff !important; }
  .collapsible-print { display: block !important; }
  tr.print-only-row { display: table-row !important; }
  th, td { white-space: normal !important; }
}
@media screen {
  .print-only { display: none !important; }
  tr.print-only-row { display: none; }
}
</style>

<style lang="scss">
@media print {
  .facture-print,
  .facture-print .overflow-hidden,
  .facture-print .overflow-x-auto,
  .facture-print .collapsible-print {
    overflow: visible !important;
    max-height: none !important;
    height: auto !important;
  }
}
</style>
